/**
 * POI (Points of Interest) Service
 * Fetches nearby POIs using the Overpass API (OpenStreetMap)
 */

export interface POICategory {
  icon: string
  iconAnchor: [number, number]
  iconSize: [number, number]
  key: string
  label: string
  overpassTags: string[]
}

export interface POIData {
  address?: string
  category: string
  categoryKey: string
  distance?: number
  id: number
  latitude: number
  longitude: number
  name: string
  openingHours?: string
}

export interface POILabels {
  [key: string]: string
  distance?: string
}

export interface POIQueryOptions {
  categories?: string[]
  latitude: number
  longitude: number
  radius: number // in meters
}

const POI_CATEGORIES_BASE: POICategory[] = [
  {
    icon: 'pin-2.svg',
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    key: 'school',
    label: 'Schule',
    overpassTags: ['amenity=school'],
  },
  {
    icon: 'pin-2.svg',
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    key: 'kindergarten',
    label: 'Kindergarten',
    overpassTags: ['amenity=kindergarten'],
  },
  {
    icon: 'pin-2.svg',
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    key: 'supermarket',
    label: 'Supermarkt',
    overpassTags: ['shop=supermarket', 'shop=convenience'],
  },
  {
    icon: 'pin-2.svg',
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    key: 'public_transport',
    label: 'ÖPNV',
    overpassTags: ['highway=bus_stop', 'railway=tram_stop', 'railway=station', 'railway=halt'],
  },
  {
    icon: 'pin-2.svg',
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    key: 'doctor',
    label: 'Arzt',
    overpassTags: ['amenity=doctors', 'amenity=clinic'],
  },
  {
    icon: 'pin-2.svg',
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    key: 'restaurant',
    label: 'Restaurant',
    overpassTags: ['amenity=restaurant'],
  },
  {
    icon: 'pin-2.svg',
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    key: 'cafe',
    label: 'Café',
    overpassTags: ['amenity=cafe'],
  },
  {
    icon: 'pin-2.svg',
    iconAnchor: [12, 12],
    iconSize: [24, 24],
    key: 'charging_station',
    label: 'Ladestation',
    overpassTags: ['amenity=charging_station'],
  }
]

class PoiService {
  private readonly cacheDuration: number = 30 * 60 * 1000 // 30 minutes
  private readonly cachePrefix = 'poi_cache_'
  private categories: POICategory[] = [...POI_CATEGORIES_BASE]
  private iconBase: string = ''
  private labels: POILabels = {}
  private readonly overpassUrl = 'https://overpass-api.de/api/interpreter'

  public clearCache (): void {
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key?.startsWith(this.cachePrefix)) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key))
    } catch {
      // Storage unavailable - silently fail
    }
  }

  public async fetchPOIs (options: POIQueryOptions): Promise<POIData[]> {
    const cacheKey = this.getCacheKey(options)
    const cached = this.getFromCache(cacheKey)

    if (cached) {
      return cached
    }

    const categories = options.categories
      ? this.categories.filter(c => options.categories!.includes(c.key))
      : this.categories

    const query = this.buildOverpassQuery(options.latitude, options.longitude, options.radius, categories)

    try {
      const response = await fetch(this.overpassUrl, {
        body: `data=${encodeURIComponent(query)}`,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        method: 'POST'
      })

      if (!response.ok) {
        throw new Error(`Overpass API error: ${response.status}`)
      }

      const data = await response.json()
      const pois = this.parseOverpassResponse(data, options.latitude, options.longitude, categories)

      this.saveToCache(cacheKey, pois)

      return pois
    } catch {
      // POIs are a non-critical enhancement; the public Overpass endpoint
      // times out often, so a failed fetch is expected and stays silent.
      return []
    }
  }

  public getCategories (): POICategory[] {
    return this.categories
  }

  public getCategory (key: string): POICategory | undefined {
    return this.categories.find(c => c.key === key)
  }

  public getDistanceLabel (): string {
    return this.labels.distance || 'Distance'
  }

  public setIconBase (base: string): void {
    this.iconBase = base
    this.rebuildCategories()
  }

  public setLabels (labels: POILabels): void {
    this.labels = labels
    this.rebuildCategories()
  }

  private buildOverpassQuery (lat: number, lng: number, radius: number, categories: POICategory[]): string {
    const tagQueries = categories.flatMap(category =>
      category.overpassTags.map(tag => {
        const [key, value] = tag.split('=')
        return `node["${key}"="${value}"](around:${radius},${lat},${lng});`
      })
    )

    return `
      [out:json][timeout:25];
      (
        ${tagQueries.join('\n        ')}
      );
      out body;
    `.trim()
  }

  private calculateDistance (lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000 // Earth's radius in meters
    const dLat = this.toRad(lat2 - lat1)
    const dLng = this.toRad(lng2 - lng1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  private formatAddress (tags?: Record<string, string>): string | undefined {
    if (!tags) return undefined

    const parts: string[] = []
    if (tags['addr:street']) {
      parts.push(tags['addr:street'])
      if (tags['addr:housenumber']) {
        parts[parts.length - 1] += ' ' + tags['addr:housenumber']
      }
    }
    if (tags['addr:postcode'] || tags['addr:city']) {
      parts.push([tags['addr:postcode'], tags['addr:city']].filter(Boolean).join(' '))
    }

    return parts.length > 0 ? parts.join(', ') : undefined
  }

  private getCacheKey (options: POIQueryOptions): string {
    const cats = options.categories?.sort().join(',') || 'all'
    return `${options.latitude.toFixed(4)}_${options.longitude.toFixed(4)}_${options.radius}_${cats}`
  }

  private getFromCache (cacheKey: string): null | POIData[] {
    try {
      const stored = sessionStorage.getItem(this.cachePrefix + cacheKey)
      if (!stored) return null

      const { data, timestamp } = JSON.parse(stored)
      if (Date.now() - timestamp > this.cacheDuration) {
        sessionStorage.removeItem(this.cachePrefix + cacheKey)
        return null
      }

      return data
    } catch {
      return null
    }
  }

  private matchCategory (tags: Record<string, string>, categories: POICategory[]): POICategory | undefined {
    for (const category of categories) {
      for (const tagQuery of category.overpassTags) {
        const [key, value] = tagQuery.split('=')
        if (tags[key] === value) {
          return category
        }
      }
    }
    return undefined
  }

  private parseOverpassResponse (
    data: { elements: Array<{ id: number, lat: number, lon: number, tags?: Record<string, string> }> },
    centerLat: number,
    centerLng: number,
    categories: POICategory[]
  ): POIData[] {
    if (!data.elements || !Array.isArray(data.elements)) {
      return []
    }

    return data.elements
      .filter(element => element.lat && element.lon)
      .map(element => {
        const category = this.matchCategory(element.tags || {}, categories)
        const distance = this.calculateDistance(centerLat, centerLng, element.lat, element.lon)

        return {
          address: this.formatAddress(element.tags),
          category: category?.label || '',
          categoryKey: category?.key || '',
          distance: Math.round(distance),
          id: element.id,
          latitude: element.lat,
          longitude: element.lon,
          name: element.tags?.name || category?.label || '',
          openingHours: element.tags?.opening_hours
        }
      })
      .sort((a, b) => (a.distance || 0) - (b.distance || 0))
  }

  private rebuildCategories (): void {
    this.categories = POI_CATEGORIES_BASE.map(category => ({
      ...category,
      icon: this.iconBase ? `${this.iconBase}${category.icon}` : category.icon,
      label: this.labels[category.key] || category.label,
    }))
  }

  private saveToCache (cacheKey: string, data: POIData[]): void {
    try {
      sessionStorage.setItem(
        this.cachePrefix + cacheKey,
        JSON.stringify({ data, timestamp: Date.now() })
      )
    } catch {
      // Storage quota exceeded or unavailable - silently fail
    }
  }

  private toRad (deg: number): number {
    return deg * (Math.PI / 180)
  }
}

export const POI_CATEGORIES = POI_CATEGORIES_BASE

export default new PoiService()
